// AreaActions — the administrative area's ONE registry and its overlays (ADR-036
// §7 — the four kinds; mirrors the exemplar `pages/teams/TeamActions.tsx`).
//
//   • `areaActs` is the PURE registry — rows with a kind, a gate and a `show`
//     predicate over the status and the closed-over facts; the shape test
//     (`components/patterns/actionGrammar.test.ts`) walks it.
//   • `useAreaActions` resolves it for the peek's target and returns the ACTS and
//     the OVERLAYS (every drawer and dialog, mounted ONCE beside the peek).
//
// An area is PAGELESS (the peek is its only surface) and the peek serves TWO
// targets in one panel — an area, or a COUNTRY (the tree's top: no id, no
// lifecycle). The rows: Add inside the primary (a country's: Create
// administrative area — a root pre-seeded to it); Rename the one edit (an area's
// only, section-less — a plain button beside the main one); the door — Move
// (this area under another parent) · Reorder (the areas inside; a country's: its
// top-level areas — offered while two or more stand to be ordered, ADR-046) ·
// Disable / Reactivate · Delete (the danger act; the server refuses a used area,
// and the reference-delete dialog names what still uses it; after a delete the
// peek steps up to where the area sat — its parent's, or its country's).

import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { AdministrativeAreaNode } from '@ems-app/schemas/administrative-areas/responses';
import { resolveActs, type ActionItem, type EntityAct } from '../../../components/patterns/entityRegistry.js';
import { useReferenceDelete } from '../../../components/patterns/ReferenceDelete.js';
import { ReorderDrawer, useReorderVerb } from '../../../components/patterns/ReorderDrawer.js';
import { Badge } from '../../../components/ui/Badge.js';
import { ConfirmDialog } from '../../../components/ui/ConfirmDialog.js';
import { icons } from '../../../components/ui/icons.js';
import { useCan } from '../../../hooks/useCan.js';
import { useEntitlements } from '../../../hooks/useEntitlements.js';
import { useEntityPeek } from '../../../hooks/useEntityPeek.js';
import { useDeleteAdministrativeArea, useReorderAdministrativeAreas, useSetAdministrativeAreaDisabled } from '../../../hooks/useAdministrativeAreaMutations.js';
import { AdministrativeAreaFormDrawer } from './AdministrativeAreaFormDrawer.js';
import { AdministrativeAreaMoveDrawer } from './AdministrativeAreaMoveDrawer.js';
import { useQuickSave } from '../../../components/patterns/forms/useQuickSave.js';

export type AreaStatus = 'active' | 'disabled';

/** What each act does — the hook wires these to its overlays. */
export interface AreaActHandlers {
  addInside: () => void;
  rename: () => void;
  move: () => void;
  reorder: () => void;
  disable: () => void;
  reactivate: () => void;
  remove: () => void;
}

/** The closed-over facts the rows read beside the status. */
export interface AreaActFacts {
  /** The peek is on a country (the tree's top) — no rename, no move, no lifecycle. */
  isCountry: boolean;
  /** Two or more areas stand inside to be ordered (the reorder verb's rule). */
  reorderable: boolean;
}

/** The area's registry — pure: the rows, their kinds and predicates. */
export function areaActs(h: AreaActHandlers, facts: AreaActFacts): ActionItem<AreaStatus>[] {
  const area = () => !facts.isCountry;
  return [
    // The primary — what people come for: a child inside this area (a country's: a root).
    { id: 'add-inside', kind: 'primary', labelKey: facts.isCountry ? 'actions.add' : 'actions.addInside', icon: icons.add, run: { handler: h.addInside }, gate: 'reference_data:manage', order: 10 },
    // The one edit — section-less (no card carries it), so a plain button in the top row.
    { id: 'rename', kind: 'edit', labelKey: 'actions.rename', icon: icons.edit, run: { handler: h.rename }, gate: 'reference_data:manage', order: 10, show: area },
    // The door — the two operations, then the lifecycle; Delete the danger act, fenced last.
    { id: 'move', kind: 'operation', labelKey: 'actions.move', icon: icons.folder, run: { handler: h.move }, gate: 'reference_data:manage', order: 10, show: area },
    { id: 'reorder', kind: 'operation', labelKey: 'common:reorder.action', icon: icons.drag, run: { handler: h.reorder }, gate: 'reference_data:manage', order: 20, show: () => facts.reorderable },
    { id: 'disable', kind: 'lifecycle', labelKey: 'actions.disable', icon: icons.statusBlocked, run: { handler: h.disable }, gate: 'reference_data:manage', order: 10, show: (ctx) => area() && ctx.status === 'active' },
    { id: 'reactivate', kind: 'lifecycle', labelKey: 'actions.reactivate', icon: icons.restore, run: { handler: h.reactivate }, gate: 'reference_data:manage', order: 10, show: (ctx) => area() && ctx.status === 'disabled' },
    { id: 'delete', kind: 'lifecycle', labelKey: 'common:reference.delete', icon: icons.delete, run: { handler: h.remove }, gate: 'reference_data:manage', order: 20, tone: 'danger', show: area },
  ];
}

export interface AreaActionsTarget {
  /** The area the peek is on — null on a country. */
  node: AdministrativeAreaNode | null;
  /** The country — the node's, or the peek's own. */
  iso: string;
  /** The whole tree (the drawers pick parents from it). */
  all: AdministrativeAreaNode[];
  /** What the peek lists inside — the node's children, or the country's roots (the reorder's items). */
  inside: AdministrativeAreaNode[];
}

export interface AreaActions {
  /** The resolved set — what the peek composes from (`ContextPanel acts`). */
  acts: EntityAct[];
  /** Every drawer and dialog, mounted ONCE by the caller beside the peek. */
  overlays: ReactNode;
}

export function useAreaActions(projectId: string, { node, iso, all, inside }: AreaActionsTarget): AreaActions {
  const { t } = useTranslation('administrative-areas');
  const can = useCan(projectId);
  const entitlements = useEntitlements(projectId);
  const { openPeek } = useEntityPeek();
  const canManage = can('reference_data:manage');
  const setDisabled = useSetAdministrativeAreaDisabled(projectId);
  // Reactivate is low-stakes and direct (no dialog) — a quick save: a refusal said once in its own words, the tree re-read.
  const quickSwitch = useQuickSave(setDisabled, { key: 'area-reactivate' });
  const saveOrder = useReorderAdministrativeAreas(projectId);
  const reorder = useReorderVerb();
  const remove = useDeleteAdministrativeArea(projectId);
  // Delete, then step up to where the area sat — its parent's peek, or its country's.
  const deletion = useReferenceDelete(async (id) => {
    const gone = all.find((n) => n.id === id);
    await remove.mutateAsync(id);
    if (gone) openPeek(gone.parent_id != null ? `administrative-areas:${gone.parent_id}` : `geo-country:${gone.country_iso_code}`);
  });
  const [addInsideOpen, setAddInsideOpen] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);
  const [moveOpen, setMoveOpen] = useState(false);
  const [disableOpen, setDisableOpen] = useState(false);

  const acts = resolveActs(
    areaActs(
      {
        addInside: () => setAddInsideOpen(true),
        rename: () => setRenameOpen(true),
        move: () => setMoveOpen(true),
        reorder: reorder.start,
        disable: () => setDisableOpen(true),
        reactivate: () => {
          if (node) void quickSwitch.save({ administrativeAreaId: node.id, disabled: false });
        },
        remove: () => {
          if (node) deletion.ask(node);
        },
      },
      { isCountry: node === null, reorderable: reorder.offered(inside.length, true) },
    ),
    { can, entitlements, status: node?.is_disabled ? 'disabled' : 'active' },
    (key) => t(key),
  );

  const overlays = canManage ? (
    <>
      {/* A country creates a ROOT (pre-seeded country, then opens the new area's peek); an area adds a CHILD (pre-seeded
          parent — the peek stays, its list refreshes in place). Keyed by the target: a peek→peek switch never keeps a seed. */}
      {node === null ? (
        <AdministrativeAreaFormDrawer key={iso} projectId={projectId} allNodes={all} presetCountry={iso} open={addInsideOpen} onClose={() => setAddInsideOpen(false)} onCreated={(id) => openPeek(`administrative-areas:${id}`)} />
      ) : (
        <>
          <AdministrativeAreaFormDrawer key={`add:${node.id}`} projectId={projectId} allNodes={all} presetParentId={node.id} open={addInsideOpen} onClose={() => setAddInsideOpen(false)} />
          <AdministrativeAreaFormDrawer key={`rename:${node.id}`} projectId={projectId} allNodes={all} node={node} open={renameOpen} onClose={() => setRenameOpen(false)} />
          <AdministrativeAreaMoveDrawer key={node.id} projectId={projectId} allNodes={all} node={node} open={moveOpen} onClose={() => setMoveOpen(false)} />
          <ConfirmDialog
            open={disableOpen}
            onClose={() => setDisableOpen(false)}
            tone="danger"
            title={t('disable.title', { name: node.name })}
            description={t('disable.body')}
            confirmLabel={t('disable.confirm')}
            onConfirm={async () => {
              await setDisabled.mutateAsync({ administrativeAreaId: node.id, disabled: true });
            }}
          />
        </>
      )}
      <ReorderDrawer
        key={node ? `node:${node.id}` : `country:${iso}`}
        {...reorder.drawer}
        items={inside}
        getId={(n) => n.id}
        getName={(n) => n.name}
        trailing={(n) =>
          n.is_disabled && (
            <Badge variant="neutral" appearance="subtle">
              {t('status.disabled')}
            </Badge>
          )
        }
        title={t('reorder.title')}
        description={t('reorder.help')}
        listLabel={t('reorder.listLabel')}
        onSave={(ordered) => {
          const orderedIds = ordered.map((n) => n.id);
          return saveOrder.mutateAsync(node ? { parentId: node.id, orderedIds } : { parentId: null, country: iso, orderedIds });
        }}
      />
      {/* Beside the drawers, not inside the area's block: the deleted area leaves the list while the dialog closes. */}
      {deletion.dialog}
    </>
  ) : null;

  return { acts, overlays };
}
